import axios from 'axios';
import { hasFirebaseAuthPersistence } from '../utils/deferUntilIdle';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_REC_API,
});

// Add Firebase token to every request when user is logged in
apiClient.interceptors.request.use(
  async (config) => {
    if (!hasFirebaseAuthPersistence()) {
      return config;
    }

    const { getAuth } = await import('../services/firebase');
    const auth = await getAuth();
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      } catch (err) {
        // Token refresh failed - continue without auth, API will return 401
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const api = {
  get(apiPath, params, authFlag, responseType) {
    return new Promise(async (resolve, reject) => {
      try {
        const args = { params: params || {} };
        if (responseType) args.responseType = responseType;

        const response = await apiClient.get(apiPath, args);
        resolve({
          data: response.data?.response ?? response.data,
        });
      } catch (e) {
        reject(e);
      }
    });
  },

  getById(apiPath, id, authFlag) {
    return apiClient.get(`${apiPath}/${id}`).then((response) => ({
      data: response.data?.response ?? response.data,
    }));
  },

  post(apiPath, data, authFlag) {
    return apiClient.post(apiPath, data).then((response) => ({
      data: response.data?.response ?? response.data,
    }));
  },

  delete(apiPath, id, authFlag) {
    return apiClient.delete(`${apiPath}/${id}`).then((response) => ({
      data: response.data?.response ?? response.data,
    }));
  },

  put(apiPath, id, data, authFlag) {
    return apiClient.put(`${apiPath}/${id}`, data).then((response) => ({
      data: response.data?.response ?? response.data,
    }));
  },

  patch(apiPath, id, data, authFlag) {
    return apiClient.patch(`${apiPath}/${id}`, data).then((response) => ({
      data: response.data?.response ?? response.data,
    }));
  },

  upload(apiPath, file, authFlag) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(apiPath, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((response) => ({
      data: response.data?.response ?? response.data,
    }));
  },
};

export default api;
